import {
  Checklist,
  ChecklistCell,
  ChecklistRow,
  ChecklistRowSource,
  ProjectsService,
  UpdateChecklistCellRequest,
  UpdateChecklistRowRequest,
  UpdateChecklistStepRequest,
} from '@beeboard/common';
import { defineStore } from 'pinia';

type ProjectId = string;
type ActivityId = string;

interface ChecklistStoreState {
  /** Loaded checklist grids, keyed by project and activity. */
  checklists: Record<ProjectId, Record<ActivityId, Checklist>>;
  /** Loaded project-wide row pools, keyed by project. */
  poolRows: Record<ProjectId, ChecklistRow[]>;
}

/**
 * Stale-response guards (non-reactive, module-level): monotonic request ids
 * per checklist grid / per project pool. Concurrent loads for the same key
 * can resolve out of order — only the response of the latest request may be
 * written to the store. Mirrors the `reloadGeneration` pattern used for the
 * full project reload in WebSocketLayer.vue.
 */
const gridRequestIds: Record<string, number> = {};
const poolRequestIds: Record<string, number> = {};

function nextRequestId(ids: Record<string, number>, key: string): number {
  ids[key] = (ids[key] ?? 0) + 1;

  return ids[key];
}

/**
 * Store for the per-activity checklist grids (WI #5577) and the
 * project-wide checklist row pool. Mutations follow the app convention of
 * "await the API call, then patch from the response or refetch" — reorders
 * refetch because the backend reindexes siblings.
 */
export const useChecklistStore = defineStore('checklistStore', {
  state: (): ChecklistStoreState => ({
    checklists: {},
    poolRows: {},
  }),

  getters: {
    checklist() {
      return (projectId: string, activityId: string): Checklist | undefined =>
        this.checklists[projectId]?.[activityId];
    },

    hasChecklist() {
      return (projectId: string, activityId: string): boolean =>
        !!this.checklists[projectId]?.[activityId];
    },

    projectPoolRows() {
      return (projectId: string): ChecklistRow[] =>
        this.poolRows[projectId] || [];
    },

    hasPoolRows() {
      return (projectId: string): boolean => !!this.poolRows[projectId];
    },
  },

  actions: {
    async loadChecklist(projectId: string, activityId: string) {
      const key = `${projectId}:${activityId}`;
      const requestId = nextRequestId(gridRequestIds, key);
      const checklist = await ProjectsService.getActivityChecklist(
        projectId,
        activityId,
      );
      // Stale response guard: a newer load started meanwhile — discard
      if (requestId !== gridRequestIds[key]) {
        return this.checklists[projectId]?.[activityId] ?? checklist;
      }
      if (!this.checklists[projectId]) {
        this.checklists[projectId] = {};
      }
      this.checklists[projectId][activityId] = checklist;

      return checklist;
    },

    /* Steps (columns) */

    async addStep(projectId: string, activityId: string, title: string) {
      await ProjectsService.createChecklistStep(projectId, activityId, {
        title,
      });
      await this.loadChecklist(projectId, activityId);
    },

    async updateStep(
      projectId: string,
      activityId: string,
      stepId: string,
      payload: UpdateChecklistStepRequest,
    ) {
      await ProjectsService.updateChecklistStep(
        projectId,
        activityId,
        stepId,
        payload,
      );
      await this.loadChecklist(projectId, activityId);
    },

    async removeStep(projectId: string, activityId: string, stepId: string) {
      await ProjectsService.deleteChecklistStep(projectId, activityId, stepId);
      await this.loadChecklist(projectId, activityId);
    },

    /* Row source */

    async updateRowSource(
      projectId: string,
      activityId: string,
      rowSource: ChecklistRowSource,
    ) {
      const checklist = await ProjectsService.updateChecklistRowSource(
        projectId,
        activityId,
        { row_source: rowSource },
      );
      // Invalidate in-flight loads — they carry pre-mutation data
      nextRequestId(gridRequestIds, `${projectId}:${activityId}`);
      if (!this.checklists[projectId]) {
        this.checklists[projectId] = {};
      }
      this.checklists[projectId][activityId] = checklist;

      return checklist;
    },

    /* Individual rows */

    async addRows(projectId: string, activityId: string, names: string[]) {
      await ProjectsService.createChecklistRows(projectId, activityId, {
        names,
      });
      await this.loadChecklist(projectId, activityId);
    },

    async updateRow(
      projectId: string,
      activityId: string,
      rowId: string,
      payload: UpdateChecklistRowRequest,
    ) {
      await ProjectsService.updateChecklistRow(
        projectId,
        activityId,
        rowId,
        payload,
      );
      await this.loadChecklist(projectId, activityId);
    },

    async removeRow(projectId: string, activityId: string, rowId: string) {
      await ProjectsService.deleteChecklistRow(projectId, activityId, rowId);
      await this.loadChecklist(projectId, activityId);
    },

    /* Cells */

    async updateCell(
      projectId: string,
      activityId: string,
      payload: UpdateChecklistCellRequest,
    ) {
      const cell = await ProjectsService.updateChecklistCell(
        projectId,
        activityId,
        payload,
      );
      // Invalidate in-flight loads before patching — one issued before this
      // cell committed server-side (e.g. a concurrent WS refresh) would
      // otherwise resolve later and revert the toggle. Mirrors updateRowSource.
      nextRequestId(gridRequestIds, `${projectId}:${activityId}`);
      this.writeCell(projectId, activityId, cell);

      return cell;
    },

    writeCell(projectId: string, activityId: string, cell: ChecklistCell) {
      const checklist = this.checklists[projectId]?.[activityId];
      if (!checklist) {
        return;
      }
      const index = checklist.cells.findIndex(
        (existing) =>
          existing.id === cell.id ||
          (existing.step_id === cell.step_id &&
            existing.row_id === cell.row_id),
      );
      if (index > -1) {
        checklist.cells.splice(index, 1, cell);
      } else {
        checklist.cells.push(cell);
      }
    },

    /* Project-wide row pool */

    async loadPoolRows(projectId: string) {
      const requestId = nextRequestId(poolRequestIds, projectId);
      const rows = await ProjectsService.getProjectChecklistRows(projectId);
      // Stale response guard: a newer load started meanwhile — discard
      if (requestId !== poolRequestIds[projectId]) {
        return this.poolRows[projectId] ?? rows;
      }
      this.poolRows[projectId] = rows;

      return rows;
    },

    async addPoolRows(projectId: string, names: string[]) {
      await ProjectsService.createProjectChecklistRows(projectId, { names });
      await this.loadPoolRows(projectId);
    },

    async updatePoolRow(
      projectId: string,
      rowId: string,
      payload: UpdateChecklistRowRequest,
    ) {
      await ProjectsService.updateProjectChecklistRow(
        projectId,
        rowId,
        payload,
      );
      await this.loadPoolRows(projectId);
    },

    async removePoolRow(projectId: string, rowId: string) {
      await ProjectsService.deleteProjectChecklistRow(projectId, rowId);
      await this.loadPoolRows(projectId);
    },

    /* Invalidation */

    /**
     * Drop a single activity's grid (e.g. the activity was deleted) and
     * invalidate any in-flight load so it can't re-add the stale grid.
     */
    removeActivityChecklist(projectId: string, activityId: string) {
      nextRequestId(gridRequestIds, `${projectId}:${activityId}`);
      if (this.checklists[projectId]) {
        delete this.checklists[projectId][activityId];
      }
    },

    /**
     * Drop everything cached for a project (call on leaving/switching a
     * project) so retained grids don't grow unbounded or keep getting
     * refreshed by socket events for a project the user has navigated away
     * from. In-flight loads are invalidated too.
     */
    clearProject(projectId: string) {
      Object.keys(this.checklists[projectId] ?? {}).forEach((activityId) =>
        nextRequestId(gridRequestIds, `${projectId}:${activityId}`),
      );
      nextRequestId(poolRequestIds, projectId);
      delete this.checklists[projectId];
      delete this.poolRows[projectId];
    },

    /* Realtime (socket) refresh — see WebSocketLayer.vue */

    /**
     * Another user changed the checklist of an activity: refetch it, but
     * only when it is currently loaded (i.e. someone is looking at it).
     */
    async refreshActivityChecklist(projectId: string, activityId?: string) {
      if (activityId && this.hasChecklist(projectId, activityId)) {
        await this.loadChecklist(projectId, activityId);

        return;
      }
      // The event may carry a sub-entity id (step/row/cell) instead of the
      // activity id — refresh every loaded grid of the project in that case.
      const loaded = this.checklists[projectId];
      if (!loaded) {
        return;
      }
      // allSettled: one grid pointing at a since-deleted activity (404) must
      // not abort the refresh of its siblings.
      await Promise.allSettled(
        Object.keys(loaded).map((id) => this.loadChecklist(projectId, id)),
      );
    },

    /**
     * Another user changed the project row pool: refetch the pool (when
     * loaded) and any loaded pool-mode checklist grids, which mirror it.
     */
    async refreshPoolRows(projectId: string) {
      if (this.hasPoolRows(projectId)) {
        await this.loadPoolRows(projectId);
      }
      const loaded = this.checklists[projectId];
      if (!loaded) {
        return;
      }
      await Promise.allSettled(
        Object.entries(loaded)
          .filter(([, checklist]) => checklist.row_source === 'pool')
          .map(([activityId]) => this.loadChecklist(projectId, activityId)),
      );
    },
  },
});
